import axios from 'axios';
import type {
  CacheSummary,
  CacheDetailResponse,
  CacheKeyEntry,
  EvictResultResponse,
  CacheOverviewResponse,
  CacheTier,
  PageResponse,
} from '../types/cacheAdmin';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// Request interceptor — Bearer 토큰 부착
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — ApiResponse<T> 언래핑
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? '알 수 없는 오류',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      '서버에 연결할 수 없습니다';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

export const cacheAdminApi = {
  /**
   * GET /cache-admin/caches — 캐시 목록 (CACHE_READ)
   */
  listCaches: (params: { tier?: CacheTier | ''; keyword?: string }): Promise<CacheSummary[]> => {
    const sp = new URLSearchParams();
    if (params.tier) sp.set('tier', params.tier);
    if (params.keyword) sp.set('keyword', params.keyword);
    return apiClient
      .get<unknown, { data: CacheSummary[] }>(`/cache-admin/caches?${sp.toString()}`)
      .then((res) => res.data);
  },

  /**
   * GET /cache-admin/caches/{name} — 캐시 상세 (CACHE_READ)
   */
  getCacheDetail: (name: string): Promise<CacheDetailResponse> =>
    apiClient
      .get<unknown, { data: CacheDetailResponse }>(`/cache-admin/caches/${encodeURIComponent(name)}`)
      .then((res) => res.data),

  /**
   * GET /cache-admin/caches/{name}/keys — 키스페이스 페이징 (CACHE_READ)
   */
  getCacheKeys: (params: {
    name: string;
    keyword?: string;
    page?: number;
    size?: number;
  }): Promise<PageResponse<CacheKeyEntry>> => {
    const sp = new URLSearchParams();
    if (params.keyword) sp.set('keyword', params.keyword);
    if (params.page !== undefined) sp.set('page', String(params.page));
    if (params.size !== undefined) sp.set('size', String(params.size));
    return apiClient
      .get<unknown, { data: PageResponse<CacheKeyEntry> }>(
        `/cache-admin/caches/${encodeURIComponent(params.name)}/keys?${sp.toString()}`
      )
      .then((res) => res.data);
  },

  /**
   * DELETE /cache-admin/caches/{name} — 단건 evict (CACHE_EVICT)
   */
  evictCache: (name: string): Promise<EvictResultResponse> =>
    apiClient
      .delete<unknown, { data: EvictResultResponse }>(
        `/cache-admin/caches/${encodeURIComponent(name)}`
      )
      .then((res) => res.data),

  /**
   * DELETE /cache-admin/caches?confirm=EVICT-ALL — 전체 evict (CACHE_EVICT)
   */
  evictAllCaches: (): Promise<EvictResultResponse> =>
    apiClient
      .delete<unknown, { data: EvictResultResponse }>('/cache-admin/caches?confirm=EVICT-ALL')
      .then((res) => res.data),

  /**
   * GET /cache-admin/overview — 전체 개요 (CACHE_READ)
   */
  getOverview: (): Promise<CacheOverviewResponse> =>
    apiClient
      .get<unknown, { data: CacheOverviewResponse }>('/cache-admin/overview')
      .then((res) => res.data),
};
